import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Unlock, Check } from "lucide-react";

/**
 * BondLab — an interactive "Anatomy of a Bond" visualization.
 *
 * Wordlessly explains the BetBond model:
 *   two peers → stakes flow into escrow → transparent 7% surfaces → winner takes the rest.
 *
 * Auto-loops every few seconds. The stake slider updates the math live and is
 * applied at the start of the next cycle. The winning side alternates so the
 * symmetry of "no house" is visible.
 */

const ACCENT = "#1E88E5";
const FEE_RATE = 0.07;

const formatCash = (n) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);

const STEPS = [
  { id: "bond", label: "Bond" },
  { id: "stake", label: "Stake" },
  { id: "lock", label: "Lock" },
  { id: "settle", label: "Settle" },
];

// Animation phases (durations in ms). Total ~ 5.2s per cycle.
const PHASES = [
  { id: "idle", ms: 700 },
  { id: "bond", ms: 800 },     // arc draws between the two peers
  { id: "stake", ms: 1100 },   // stakes fly into the vault
  { id: "lock", ms: 900 },     // vault locks, fee floats out
  { id: "settle", ms: 1100 },  // vault unlocks, payout flies to winner
  { id: "rest", ms: 600 },
];

const Peer = ({ side, label, highlight, won }) => {
  const isLeft = side === "left";
  return (
    <div
      className={`relative flex flex-col items-center ${
        isLeft ? "items-start sm:items-center" : "items-end sm:items-center"
      }`}
    >
      <motion.div
        animate={{
          scale: highlight ? 1.06 : 1,
          boxShadow: highlight
            ? `0 0 0 2px ${ACCENT}, 0 0 28px rgba(30,136,229,0.55)`
            : "0 0 0 1px rgba(255,255,255,0.10)",
        }}
        transition={{ duration: 0.35 }}
        className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-white/[0.08] to-white/[0.02] flex items-center justify-center"
      >
        <span className="text-white font-bold text-sm sm:text-base select-none">
          {isLeft ? "A" : "B"}
        </span>

        <AnimatePresence>
          {won && (
            <motion.div
              key="winner-check"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 18 }}
              className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#1E88E5] flex items-center justify-center shadow-[0_0_12px_rgba(30,136,229,0.7)]"
            >
              <Check size={12} className="text-white" strokeWidth={3} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      <span className="mt-2 text-[11px] uppercase tracking-wider text-gray-500">
        {label}
      </span>
    </div>
  );
};

const Vault = ({ locked, settling }) => (
  <motion.div
    animate={{
      scale: locked ? 1.05 : 1,
      boxShadow: locked
        ? "0 0 0 1px rgba(30,136,229,0.55), 0 0 36px rgba(30,136,229,0.35)"
        : "0 0 0 1px rgba(255,255,255,0.10)",
    }}
    transition={{ duration: 0.35 }}
    className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-white/[0.06] to-white/[0.01] flex items-center justify-center"
  >
    <AnimatePresence mode="wait" initial={false}>
      {locked || settling ? (
        <motion.div
          key="locked"
          initial={{ rotate: -8, opacity: 0 }}
          animate={{ rotate: 0, opacity: 1 }}
          exit={{ rotate: 8, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="text-[#1E88E5]"
        >
          <Lock size={22} strokeWidth={2.25} />
        </motion.div>
      ) : (
        <motion.div
          key="unlocked"
          initial={{ rotate: 8, opacity: 0 }}
          animate={{ rotate: 0, opacity: 1 }}
          exit={{ rotate: -8, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="text-gray-500"
        >
          <Unlock size={22} strokeWidth={2.25} />
        </motion.div>
      )}
    </AnimatePresence>
    <span className="absolute -bottom-5 text-[10px] uppercase tracking-[0.18em] text-gray-500">
      Escrow
    </span>
  </motion.div>
);

const BondLab = () => {
  const [stake, setStake] = useState(1000);
  const stakeRef = useRef(stake);
  stakeRef.current = stake;

  // Cycle state: which phase we're in + the stake/winner locked in for *this* cycle.
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [cycleStake, setCycleStake] = useState(1000);
  const [winner, setWinner] = useState("left"); // alternates each cycle
  const cycleCount = useRef(0);

  useEffect(() => {
    const phase = PHASES[phaseIdx];
    const t = setTimeout(() => {
      setPhaseIdx((prev) => {
        const next = (prev + 1) % PHASES.length;
        if (next === 0) {
          // start of a new cycle: snapshot the current slider value + flip winner
          cycleCount.current += 1;
          setCycleStake(stakeRef.current);
          setWinner((w) => (w === "left" ? "right" : "left"));
        }
        return next;
      });
    }, phase.ms);
    return () => clearTimeout(t);
  }, [phaseIdx]);

  const phase = PHASES[phaseIdx].id;

  const pot = cycleStake * 2;
  const fee = Math.round(pot * FEE_RATE);
  const payout = pot - fee;

  // For live numbers the user sees while sliding, show current slider math.
  const livePot = stake * 2;
  const liveFee = Math.round(livePot * FEE_RATE);
  const livePayout = livePot - liveFee;

  const showBond = ["bond", "stake", "lock", "settle"].includes(phase);
  const locked = phase === "lock";
  const settling = phase === "settle";
  const showWinner = phase === "settle" || phase === "rest";

  // Map the current animation phase onto the four labelled steps so the stepper
  // tracks the action live and fills what used to be empty space at the top.
  const activeStep =
    phase === "bond"
      ? 0
      : phase === "stake"
      ? 1
      : phase === "lock"
      ? 2
      : phase === "settle" || phase === "rest"
      ? 3
      : -1;

  const animationKey = useMemo(
    () => `${cycleCount.current}-${cycleStake}-${winner}`,
    [cycleStake, winner]
  );

  return (
    <section className="relative overflow-hidden bg-black pt-20 pb-16 border-t border-white/5">
      <div
        className="absolute left-1/2 top-8 h-80 w-[72%] -translate-x-1/2 rounded-full bg-[#1E88E5]/10 blur-[110px]"
        aria-hidden
      />
      <div className="container relative mx-auto px-4 max-w-3xl">
        <div className="text-center mb-8">
          <p className="mb-3 text-[10px] uppercase tracking-[0.26em] text-[#64B5F6]">
            Platform flow
          </p>
          <p className="mx-auto max-w-xl text-gray-500 text-sm leading-relaxed">
            A P2P bond connects two sides, holds the agreed amount in escrow,
            reveals the platform fee, and settles by the rules shown in the app.
          </p>
        </div>

        <div
          className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/[0.04] via-white/[0.02] to-transparent px-5 sm:px-8 pt-8 pb-6"
          style={{
            boxShadow:
              "0 34px 110px -52px rgba(30,136,229,0.9), inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          <div
            className="absolute inset-x-10 top-0 h-40 bg-[#1E88E5]/10 blur-[70px]"
            aria-hidden
          />
          {/* Phase stepper — tracks the live animation */}
          <div className="relative mb-6 flex items-center justify-center">
            {STEPS.map((s, i) => {
              const active = i === activeStep;
              const done = activeStep > i;
              return (
                <React.Fragment key={s.id}>
                  <div className="flex items-center gap-1.5">
                    <motion.span
                      animate={{
                        scale: active ? 1.15 : 1,
                        backgroundColor: active || done ? ACCENT : "#27272a",
                      }}
                      transition={{ duration: 0.3 }}
                      className="h-2 w-2 rounded-full"
                      style={{
                        boxShadow: active
                          ? "0 0 10px rgba(30,136,229,0.8)"
                          : "none",
                      }}
                    />
                    <span
                      className={`text-[10px] sm:text-[11px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                        active
                          ? "text-white"
                          : done
                          ? "text-gray-400"
                          : "text-gray-600"
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <span className="mx-2 h-px w-4 sm:w-8 bg-white/10" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {/* Stage */}
          <div className="relative h-[150px] sm:h-[170px]">
            {/* Connecting bond arc */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 400 200"
              preserveAspectRatio="none"
              aria-hidden
            >
              <defs>
                <linearGradient id="bondGrad" x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0%" stopColor="#1E88E5" stopOpacity="0.05" />
                  <stop offset="50%" stopColor="#1E88E5" stopOpacity="0.85" />
                  <stop offset="100%" stopColor="#1E88E5" stopOpacity="0.05" />
                </linearGradient>
              </defs>
              <motion.path
                key={`arc-${animationKey}`}
                d="M 40 100 Q 200 30 360 100"
                fill="none"
                stroke="url(#bondGrad)"
                strokeWidth="1.5"
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={
                  showBond
                    ? { pathLength: 1, opacity: 1 }
                    : { pathLength: 0, opacity: 0 }
                }
                transition={{ duration: 0.7, ease: "easeOut" }}
              />
            </svg>

            {/* Peers + Vault, absolutely positioned for clean travel paths */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 pl-1 sm:pl-3">
              <Peer
                side="left"
                label="You"
                highlight={phase === "stake" || phase === "bond"}
                won={showWinner && winner === "left"}
              />
            </div>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 pr-1 sm:pr-3">
              <Peer
                side="right"
                label="Counterparty"
                highlight={phase === "stake" || phase === "bond"}
                won={showWinner && winner === "right"}
              />
            </div>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <Vault locked={locked} settling={settling} />
            </div>

            {/* Stake chips flying in */}
            <AnimatePresence>
              {phase === "stake" && (
                <>
                  <motion.div
                    key={`chip-l-${animationKey}`}
                    initial={{ left: "10%", top: "50%", opacity: 0, scale: 0.6 }}
                    animate={{ left: "50%", top: "50%", opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.4 }}
                    transition={{ duration: 0.9, ease: "easeInOut" }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#1E88E5]/40 bg-[#1E88E5]/10 px-2 py-0.5 text-[10px] font-semibold text-[#64B5F6] shadow-[0_0_20px_rgba(30,136,229,0.25)]"
                  >
                    {formatCash(cycleStake)}
                  </motion.div>
                  <motion.div
                    key={`chip-r-${animationKey}`}
                    initial={{ left: "90%", top: "50%", opacity: 0, scale: 0.6 }}
                    animate={{ left: "50%", top: "50%", opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.4 }}
                    transition={{ duration: 0.9, ease: "easeInOut", delay: 0.05 }}
                    className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#1E88E5]/40 bg-[#1E88E5]/10 px-2 py-0.5 text-[10px] font-semibold text-[#64B5F6] shadow-[0_0_20px_rgba(30,136,229,0.25)]"
                  >
                    {formatCash(cycleStake)}
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            {/* Fee chip floating out (upward) during lock phase */}
            <AnimatePresence>
              {phase === "lock" && (
                <motion.div
                  key={`fee-${animationKey}`}
                  initial={{ left: "50%", top: "50%", opacity: 0, scale: 0.7 }}
                  animate={{ left: "50%", top: "8%", opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="absolute -translate-x-1/2 rounded-full border border-white/10 bg-black/55 px-2 py-0.5 text-[10px] font-semibold text-gray-300 backdrop-blur"
                >
                  Platform fee 7% · {formatCash(fee)}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Payout chip flying to winner */}
            <AnimatePresence>
              {phase === "settle" && (
                <motion.div
                  key={`pay-${animationKey}`}
                  initial={{ left: "50%", top: "50%", opacity: 0, scale: 0.6 }}
                  animate={{
                    left: winner === "left" ? "10%" : "90%",
                    top: "50%",
                    opacity: 1,
                    scale: 1.05,
                  }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  transition={{ duration: 1.0, ease: "easeInOut" }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 px-2.5 py-1 rounded-full text-[11px] font-bold text-white bg-[#1E88E5] shadow-[0_0_20px_rgba(30,136,229,0.55)]"
                >
                  {formatCash(payout)}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Live math row */}
          <div className="mt-6 grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
            <Stat label="Each side" value={formatCash(stake)} />
            <Stat label="Pot" value={formatCash(livePot)} />
            <Stat label="Fee 7%" value={formatCash(liveFee)} muted />
            <Stat label="Winner takes" value={formatCash(livePayout)} accent />
          </div>

          {/* Slider */}
          <div className="mt-5">
            <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1.5">
              <span>Stake per side</span>
              <span className="text-gray-400 font-medium">
                {formatCash(stake)}
              </span>
            </div>
            <input
              type="range"
              min={250}
              max={50000}
              step={250}
              value={stake}
              onChange={(e) => setStake(Number(e.target.value))}
              className="w-full bond-range accent-[#1E88E5]"
              aria-label="Stake per side"
            />
            <div className="flex justify-between text-[10px] text-gray-600 mt-1">
              <span>250</span>
              <span>50,000</span>
            </div>
          </div>

          <p className="mt-4 text-center text-[11px] leading-relaxed text-gray-600">
            Demo for illustration. Real amounts, limits, escrow timing, and outcome
            rules live in the app and always win over this page.
          </p>
        </div>
      </div>

      <style>{`
        .bond-range {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          background: linear-gradient(to right, rgba(30,136,229,0.85), rgba(30,136,229,0.15));
          border-radius: 9999px;
          outline: none;
        }
        .bond-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 9999px;
          background: #1E88E5;
          border: 2px solid #0b0b0b;
          box-shadow: 0 0 0 1px rgba(30,136,229,0.5), 0 0 14px rgba(30,136,229,0.45);
          cursor: pointer;
          transition: transform 0.15s ease;
        }
        .bond-range::-webkit-slider-thumb:hover { transform: scale(1.1); }
        .bond-range::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 9999px;
          background: #1E88E5;
          border: 2px solid #0b0b0b;
          box-shadow: 0 0 0 1px rgba(30,136,229,0.5), 0 0 14px rgba(30,136,229,0.45);
          cursor: pointer;
        }
      `}</style>
    </section>
  );
};

const Stat = ({ label, value, accent, muted }) => (
  <div className="rounded-2xl border border-white/10 bg-black/35 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)]">
    <div
      className={`text-sm sm:text-base font-semibold ${
        accent ? "text-[#64B5F6]" : muted ? "text-gray-400" : "text-white"
      }`}
    >
      {value}
    </div>
    <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">
      {label}
    </div>
  </div>
);

export default BondLab;
